import React from 'react';
import c from 'classnames';
import type { PluginSimple } from 'markdown-it';
import { baseKeymap } from 'prosemirror-commands';
import { dropCursor } from 'prosemirror-dropcursor';
import { buildKeymap } from 'prosemirror-example-setup';
import { gapCursor } from 'prosemirror-gapcursor';
import { history, redo, undo } from 'prosemirror-history';
import { inputRules as _inputRules } from 'prosemirror-inputrules';
import type { MarkdownParser, ParseSpec } from 'prosemirror-markdown';
import { MarkSpec, NodeSpec, Schema } from 'prosemirror-model';
import { EditorState, Plugin } from 'prosemirror-state';
import { EditorView, NodeViewConstructor } from 'prosemirror-view';
import { redo as yRedo, undo as yUndo, ySyncPlugin, yUndoPlugin } from 'y-prosemirror';

import { WithTranslation, withTranslation } from '@lyearn/i18n';
import { getPMDocFromHtml } from '@lyearn/lygo-blocks/src/utils';

import BaseExtension from '../BaseExtension';
import getSchema from '../ExtensionManager';
import keymap from '../ExtensionManager/keymap';
import { EditorOptions, PerformanceType } from '../ExtensionManager/types';
import { editorStatsPlugin, getBlockWords } from '../Plugin/editorStatsPlugin';
import { nodePerformanceMapPlugin } from '../Plugin/nodePerformanceMapPlugin';
import { portalKeyPlugin } from '../Plugin/portalKeyPlugin';
import { getProseMirrorDocumentDuration } from '../Plugin/prosemirrorDocumentDuration';
import yCursorPlugin from '../Plugin/yCursorPlugin';
import { getCreateReactNodeView } from '../ReactNodeView';
import { IReactNodeViewPortalsContext, ReactNodeViewPortalsContext } from '../ReactNodeViewPortals';
import { getHTMLFromDoc } from '../utils';
import { editableEditorStyles, readOnlyEditorStyles } from './styles';
import { NewCoreEditorProps } from './types';

type State = {
  isEditorFocused: boolean;
};

class Editor extends React.PureComponent<NewCoreEditorProps & WithTranslation, State> {
  private view?: EditorView;

  static defaultProps: NewCoreEditorProps = {
    autoFocus: true,
    readOnly: false,
  };

  state: State = {
    isEditorFocused: false,
  };

  extensions: { new (): BaseExtension }[] = [];
  schemaElements: ReturnType<typeof getSchema>;
  editorViewRef: React.RefObject<HTMLDivElement> = React.createRef();
  static contextType = ReactNodeViewPortalsContext;
  editorView: EditorView;
  schema: Schema;
  markdownParser: MarkdownParser;
  pasteMarkdownParser: MarkdownParser;
  plugins: Plugin[];
  inputRules: ReturnType<typeof getSchema>['inputRules'];
  nodes: { [key in string]: NodeSpec };
  marks: { [key in string]: MarkSpec };
  nodeViews: {
    [node: string]: NodeViewConstructor;
  };
  markdownTokens: Record<string, ParseSpec>;
  nodePerformance: Map<string, PerformanceType>;
  markdownRules: PluginSimple[];
  portalApi: IReactNodeViewPortalsContext;
  createReactNodeView: ReturnType<typeof getCreateReactNodeView>;
  editorOptions: EditorOptions;
  duration: number;
  wordsStats: { words: number; characters: number };

  public constructor(props: NewCoreEditorProps & WithTranslation) {
    super(props);
  }

  public componentDidMount(): void {
    this.loadEditor1();
  }

  private loadEditor1() {
    this.extensions = this.props.extensions || [];
    this.schemaElements = getSchema(this.extensions);
    this.nodes = this.createNodes();
    this.marks = this.createMarks();
    this.schema = this.createSchema();
    this.inputRules = this.createInputRules();
    this.nodePerformance = this.createNodePerformance();
    this.markdownTokens = this.createMarkdownTokens();
    this.portalApi = this.createPortalApi();
    this.createReactNodeView = this.createReactNodeViewInitialize();
    this.editorOptions = this.createEditorOptions();
    this.plugins = this.createPlugins();
    this.view = this.createEditorView();
    this.duration = this.createDuration();
    this.wordsStats = this.createWordsStats();
  }

  private createNodes() {
    return this.schemaElements.nodes;
  }

  private createMarks() {
    return this.schemaElements.marks;
  }

  private createInputRules() {
    return this.schemaElements.inputRules;
  }

  private createNodePerformance() {
    return this.schemaElements.nodePerformanceMap;
  }

  private createMarkdownTokens() {
    return this.schemaElements.markdownTokens;
  }

  private createSchema() {
    return new Schema({
      nodes: this.nodes,
      marks: this.marks,
    });
  }

  private createPortalApi() {
    const portalApi = this.context as IReactNodeViewPortalsContext;
    return portalApi;
  }

  private createReactNodeViewInitialize() {
    const { createPortal, destroyPortal } = this.portalApi;
    return getCreateReactNodeView(createPortal, destroyPortal);
  }

  private createEditorOptions() {
    return {
      schema: this.schema,
      createReactNodeView: this.createReactNodeView,
      portalApi: this.portalApi,
      t: this.props.t,
      isLocalizedContent: false,
      isMicroLearning: false,
    };
  }

  private createUndoRedoPlugins() {
    if (this.props.yProvider) {
      const type = this.props.yProvider?.doc.getXmlFragment('prosemirror');
      return [
        ySyncPlugin(type!),
        yCursorPlugin(this.props.yProvider.awareness),
        yUndoPlugin(),
        keymap({ 'Mod-z': yUndo, 'Mod-y': yRedo, 'Mod-Shift-z': yRedo }),
      ];
    }

    return [history(), keymap({ 'Mod-z': undo, 'Mod-y': redo, 'Mod-Shift-z': redo })];
  }

  private createPlugins() {
    const schemaPlugins = this.schemaElements.plugins;
    const undoRedoPlugins = this.createUndoRedoPlugins();
    const propsPlugins = this.props.plugins || [];

    return [
      ...undoRedoPlugins,
      keymap({ ...buildKeymap(this.schema), ...this.props.keymaps }),
      keymap(baseKeymap),
      dropCursor(),
      gapCursor(),
      _inputRules({
        rules: this.inputRules?.length
          ? this.inputRules?.flatMap((rule) => rule(this.editorOptions))
          : [],
      }),
      nodePerformanceMapPlugin(this.nodePerformance),
      ...schemaPlugins?.flatMap((plugin) =>
        typeof plugin === 'function' ? plugin?.(this.editorOptions) : plugin,
      ),
      ...propsPlugins.flatMap((plugin) =>
        typeof plugin === 'function' ? plugin?.(this.editorOptions) : plugin,
      ),
      portalKeyPlugin(this.portalApi),
      editorStatsPlugin(),
    ];
  }

  private createState(value?: string) {
    const doc = this.props.jsonValue
      ? this.schema.nodeFromJSON(this.props.jsonValue)
      : this.props.getPMDocFromMarkdown
      ? this.props.getPMDocFromMarkdown({
          markdownTokens: this.markdownTokens,
          schema: this.schema,
          value: value || '',
        })
      : getPMDocFromHtml(value || '', this.schema);

    return EditorState.create({
      plugins: this.plugins,
      schema: this.schema,
      doc: doc ?? undefined,
    });
  }

  private handleEditorBlur = (view: EditorView, event: FocusEvent) => {
    this.props.onBlur?.(view);
    return false;
  };

  private handleEditorFocus = (view: EditorView, event: FocusEvent) => {
    this.props.onFocus?.(view);
    return false;
  };

  private valueFromState(state: EditorState) {
    return this.props.jsonValue ? state.doc.toJSON() : getHTMLFromDoc(state.doc, this.schema);
  }

  private handleHTMLChange = (state: EditorState) => {
    if (this.props.onChange) {
      const value = this.valueFromState(state);
      if (value !== this.props.value) {
        this.props.onChange(state, value);
      }
    }
  };

  private handleJSONChange = (state: EditorState) => {
    if (this.props.onChangeJSON) {
      const isDocEqual = state.doc.eq(this.schema.nodeFromJSON(this.props.jsonValue));
      if (!isDocEqual) {
        const value = this.valueFromState(state);
        this.props.onChangeJSON(state, value);
      }
    }
  };

  private handleChange = (state: EditorState) => {
    this.handleHTMLChange(state);
    this.handleJSONChange(state);
  };

  private createDuration() {
    const doc = this.view?.state.doc;
    if (!doc) return 0;
    return getProseMirrorDocumentDuration({ proseMirrorDocument: doc });
  }

  private handleDurationChange() {
    const newDuration = this.createDuration();
    if (newDuration !== this.duration) {
      this.duration = newDuration;
      this.props.onDurationChange?.(newDuration);
    }
  }

  private createWordsStats() {
    const doc = this.view?.state.doc;

    if (!doc)
      return {
        words: 0,
        characters: 0,
      };

    return getBlockWords(doc);
  }

  private handleWordsStatsChange() {
    const newWordsStats = this.createWordsStats();
    if (newWordsStats !== this.wordsStats) {
      this.wordsStats = newWordsStats;
      this.props.onWordsStatsChange?.(newWordsStats);
    }
  }

  private createEditorView() {
    if (!this.editorViewRef.current) {
      throw new Error('createView called before ref available');
    }
    const self = this;
    const view = new EditorView(this.editorViewRef.current, {
      handleDOMEvents: {
        blur: this.handleEditorBlur,
        focus: this.handleEditorFocus,
      },
      state: this.createState(this.props.value),
      editable: () => !this.props.readOnly,
      dispatchTransaction(transaction) {
        const { state } = (this.state as EditorState).applyTransaction(transaction);

        // @ts-ignore
        if (state && this.docView) {
          // @ts-ignore
          this.updateState(state);
          self.handleChange(state);
          self.handleDurationChange();
          self.handleWordsStatsChange();
        }

        self.forceUpdate();
      },
    });

    this.props.onCreateEditorView?.(view);
    return view;
  }

  componentDidUpdate(
    prevProps: Readonly<NewCoreEditorProps & WithTranslation<'translation', undefined>>,
    prevState: Readonly<State>,
    snapshot?: any,
  ): void {
    if (!this.view) return;

    if (this.props.value !== prevProps.value) {
      const isValueChanged = this.props.value !== getHTMLFromDoc(this.view.state.doc, this.schema);

      if (isValueChanged) {
        this.view.updateState(this.createState(this.props.value));
      }

      // no transactions are dispatched when the editor is readOnly
      if (this.props.readOnly) {
        this.handleDurationChange();
        this.handleWordsStatsChange();
      }
    }

    if (this.props.jsonValue !== prevProps.jsonValue) {
      const isJSONValueChanged = !this.view.state.doc.eq(
        this.schema.nodeFromJSON(this.props.jsonValue),
      );

      if (isJSONValueChanged) {
        this.view.updateState(this.createState(this.props.jsonValue));
      }

      if (this.props.readOnly) {
        this.handleDurationChange();
        this.handleWordsStatsChange();
      }
    }

    if (this.props.readOnly !== prevProps.readOnly) {
      this.view.update({
        ...this.view?.props,
        editable: () => !this.props.readOnly,
      });
    }
  }

  componentWillUnmount() {
    this.view?.destroy();
    this.view = undefined;
  }

  render() {
    return (
      <div
        ref={this.editorViewRef}
        className={c(
          this.view?.editable ? 'Prosemirror-Editable' : 'Prosemirror-Readonly',
          this.view?.editable
            ? editableEditorStyles(this.props.t('Lygo:Menu.math_display.WriteYourEquationInKaTex'))
            : readOnlyEditorStyles(this.props.t('Lygo:Menu.math_display.WriteYourEquationInKaTex')),
          'lygo-editor',
        )}
      />
    );
  }
}

const EditorWithI18n = withTranslation('Lygo')(Editor);

export { EditorWithI18n as NewEditor };
